import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { SwapHoriz } from '@mui/icons-material';

interface PeriodOwner {
  owner_name: string;
  type: string;
  percentage: number | null;
  role?: string;
}

interface OwnershipPeriod {
  enrollment_id: string;
  facility_name: string;
  start_month: string;
  end_month: string;
  is_current: boolean;
  direct_owners: PeriodOwner[];
  indirect_owners: PeriodOwner[];
  managerial_control: PeriodOwner[];
}

interface ChowEvent {
  effective_date: string;
  seller_name: string;
  buyer_name: string;
  chow_type: string;
  chow_type_text: string;
}

type TimelineNode = { type: 'period'; data: OwnershipPeriod } | { type: 'chow'; data: ChowEvent };

interface OwnershipTimelineProps {
  ownershipPeriods: OwnershipPeriod[];
  chowEvents?: ChowEvent[];
}

const fmtMonth = (d: string) => {
  const [y, m] = d.split('-');
  return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m) - 1]} ${y}`;
};

const fmtDate = (d: string) => {
  const [y, m, day] = d.split('-');
  return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m) - 1]} ${parseInt(day)}, ${y}`;
};

export default function OwnershipTimeline({ ownershipPeriods, chowEvents }: OwnershipTimelineProps) {
  if (!ownershipPeriods || ownershipPeriods.length === 0) return null;

  const sortedChows = [...(chowEvents || [])].sort(
    (a, b) => b.effective_date.localeCompare(a.effective_date)
  );

  const timeline: TimelineNode[] = [];
  const usedChows = new Set<string>();

  ownershipPeriods.forEach((period) => {
    timeline.push({ type: 'period', data: period });
    const matchingChow = sortedChows.find((e) => {
      if (usedChows.has(e.effective_date)) return false;
      const cm = e.effective_date.substring(0, 7);
      return cm >= period.start_month && cm <= period.end_month;
    });
    if (matchingChow) {
      usedChows.add(matchingChow.effective_date);
      timeline.push({ type: 'chow', data: matchingChow });
    }
  });

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5, mb: 0.75, display: 'block' }}>
        Ownership History
      </Typography>
      <Box
        sx={{
          position: 'relative',
          pl: 2.5,
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 7,
            top: 4,
            bottom: 4,
            width: 2,
            backgroundColor: 'grey.300',
          },
        }}
      >
        {timeline.map((node) => {
          if (node.type === 'period') {
            const p = node.data as OwnershipPeriod;
            const allOwners = [...p.direct_owners, ...p.indirect_owners];
            const maxShow = 4;
            const shown = allOwners.slice(0, maxShow);
            const extra = allOwners.length - maxShow;
            const ownerSummary = shown.map(o =>
              `${o.owner_name}${o.percentage != null ? ` ${o.percentage.toFixed(0)}%` : ''}`
            ).join(', ') + (extra > 0 ? `, +${extra} more` : '');
            const dotColor = p.is_current ? '#4caf50' : '#bdbdbd';
            return (
              <Box key={p.enrollment_id} sx={{ position: 'relative', py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ position: 'absolute', left: -17, top: '50%', transform: 'translateY(-50%)', width: 8, height: 8, borderRadius: '50%', backgroundColor: dotColor, zIndex: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120, flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {fmtMonth(p.start_month)} – {p.is_current ? 'Present' : fmtMonth(p.end_month)}
                </Typography>
                {p.is_current && <Chip label="Current" size="small" color="success" variant="outlined" sx={{ height: 18, flexShrink: 0, '& .MuiChip-label': { px: 0.5, fontSize: '0.6rem' } }} />}
                <Typography variant="caption" fontWeight={600} sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ownerSummary || p.facility_name}
                </Typography>
              </Box>
            );
          } else {
            const c = node.data as ChowEvent;
            return (
              <Box key={`chow-${c.effective_date}`} sx={{ position: 'relative', py: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ position: 'absolute', left: -18, top: '50%', transform: 'translateY(-50%) rotate(45deg)', width: 6, height: 6, backgroundColor: '#ff9800', zIndex: 1 }} />
                <Chip
                  icon={<SwapHoriz sx={{ fontSize: 12 }} />}
                  label="CHOW"
                  size="small"
                  sx={{ height: 18, backgroundColor: '#ff9800', color: 'white', fontWeight: 700, fontSize: '0.6rem', '& .MuiChip-icon': { color: 'white' }, '& .MuiChip-label': { px: 0.5 } }}
                />
                <Typography variant="caption" fontWeight={600} sx={{ color: '#e65100' }}>{fmtDate(c.effective_date)}</Typography>
                <Typography variant="caption" color="text.secondary">{c.seller_name} &#8594; {c.buyer_name}</Typography>
              </Box>
            );
          }
        })}
      </Box>
    </Box>
  );
}
